import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { fetchAthleteProfileByEmail } from "../../../store/actions/athleteActions";
import { clearBusinessCacheInformation, fetchCacheInformation } from "../../../store/actions/businessActions";
import { fetchAnalytics } from "../../../store/actions/userActions";
import { fetchMyCircle } from "../../../store/actions/circleActions";
import { refreshEntitlement } from "../../../store/actions/entitlementRefresh";
import { getProfileCompletionInfo, useCodeMaps } from "../../../utils/utils";
import PlanBadge, { usePlan } from "../../common/PlanBadge";
import { buildProfileModel } from "../PlayerProfile/profileModel";
import ProfileBody, { profileSubtitle, watchBrandMatches } from "./ProfileBody";
import { useProfileTabParam } from "./performance/profileTabs";
import { SocialLinks, ValueContextModal } from "./profileSections";
import { ProfileIdentity } from "./mobile/mobileProfileSections";
import { Btn, Card, Loading, PageHeader } from "../../ForBrands/ui/BrandUI";
import { MobileCard } from "../../ForBrands/ui/mobile/MobileUI";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";

// Athlete Mode · My Profile V2 (Figma 02 · My Profile - V2). The athlete's
// own analytics page in the V2 design system. The sections under the
// header (CacheScore, contributions, CacheValue / CacheNILx, NILx rate
// card, CacheValue in context, profile + circle, season box score with
// shooting efficiency and shot zones, current season, visibility
// footprint, brand alignment) live in ProfileBody, which the read-only
// profile other roles open (AthleteProfileView) renders too, so both show
// the same numbers from the same profile model.
//
// On a phone (Figma 2000:373) there is no PageHeader: the identity row with
// "Edit my profile" (primary) and "Share profile" (secondary) opens the
// Overview tab, and SocialLinks and MetaChips are left out (the subtitle
// and the Visibility tab carry them).
//
// Beside the name, PlanBadge shows a paid plan (Kobi, 2026-10-07): an
// orange "Premium", plus "Team plan" when the team's coach pays for it.
// Nothing shows for the free plan or before the entitlement has loaded. The
// entitlement is fetched again on mount, so a coach's plan change shows
// without a reload.
//
// On desktop the sections under the rate card sit in tabs (Performance,
// Visibility, Value); the open one lives in ?tab=, the key the phone tabs
// use too (useProfileTabParam).

const PREMIUM = ["athlete_premium"];

// Two 52px buttons side by side; the tighter padding lets both labels fit a
// 320px screen (the text is centred, so 390px looks the same).
const PHONE_BTN = "min-w-0 flex-1 !px-3";

const AthleteProfilePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const athlete = useSelector((state) => state.athlete?.data || {});
  const cacheInformation = useSelector((state) => state.business?.cacheInformation || {});
  const codes = useSelector((state) => state.codes?.allCodes);
  const codeMap = useCodeMaps(codes);
  const [loading, setLoading] = useState(true);
  const [circle, setCircle] = useState([]);
  // null = still matching, [] = none / unavailable.
  const [matches, setMatches] = useState(null);
  const [contextOpen, setContextOpen] = useState(false);
  const phone = usePhoneTheme();
  const plan = usePlan();
  const [tab, setTab] = useProfileTabParam();

  // Fetch the entitlement again (the app's fetchEntitlement, on its own
  // store), so Premium a coach's new plan includes, or one that ended,
  // shows on arrival. The app fetches it on load, so this is a no-op until
  // then.
  useEffect(() => {
    refreshEntitlement();
  }, []);

  useEffect(() => {
    const email = localStorage.getItem("myEmail");
    // The athlete doc is refetched below and the page waits for it; clearing
    // it first would also blank the header's name and photo meanwhile.
    dispatch(clearBusinessCacheInformation());
    setLoading(true);
    if (!email) {
      setLoading(false);
      return undefined;
    }
    Promise.all([dispatch(fetchAthleteProfileByEmail(email)), dispatch(fetchCacheInformation(email))]).finally(() => setLoading(false));
    dispatch(fetchMyCircle())
      .then((members) => setCircle(members || []))
      .catch(() => setCircle([]));
    dispatch(fetchAnalytics());
    return watchBrandMatches(email, setMatches);
  }, [dispatch]);

  const model = useMemo(
    () => buildProfileModel(athlete, cacheInformation, codeMap, circle, { selfView: true }),
    [athlete, cacheInformation, codeMap, circle],
  );

  if (loading) return <Loading label="Loading your profile..." />;

  const { percentage, missingFields } = getProfileCompletionInfo(athlete);
  const onlyMessage = Object.keys(cacheInformation).length === 1 && cacheInformation.message;
  const notCompleted = onlyMessage === "Athlete Profile Information is not completed.";
  const scorePending = onlyMessage === "CacheScore is not calculated.";
  const editProfile = () => navigate("/athlete/profile/edit");

  // Not scored yet: the identity row and the same card, without the tabs.
  if ((notCompleted || scorePending) && phone) {
    return (
      <div className="flex flex-col gap-[18px]">
        <ProfileIdentity
          model={model}
          subtitle={notCompleted ? `Profile ${percentage}% complete` : "Score pending"}
          badge={<PlanBadge plan={plan} />}
          actions={
            scorePending ? (
              <Btn size="lg" variant="secondary" className={PHONE_BTN} onClick={editProfile}>
                Edit my profile
              </Btn>
            ) : null
          }
        />
        <MobileCard>
          {notCompleted ? (
            <>
              <p className="bm-body-m text-bm-muted">Finish your profile setup to unlock your CacheScore™, valuation and brand matches.</p>
              {missingFields.length > 0 && (
                <ul className="flex flex-col gap-2">
                  {missingFields.map((field) => (
                    <li key={field} className="flex items-center gap-2 bm-body-m text-bm-ink">
                      <span className="h-[6px] w-[6px] shrink-0 rounded-full bg-bm-orange" />
                      {field}
                    </li>
                  ))}
                </ul>
              )}
              <Btn size="lg" className="w-full" onClick={editProfile}>
                Complete profile
              </Btn>
            </>
          ) : (
            <p className="bm-body-m text-bm-muted">Your CacheScore™ is being calculated. Check back shortly; your profile, deals and Circle keep working meanwhile.</p>
          )}
        </MobileCard>
      </div>
    );
  }

  if (notCompleted || scorePending) {
    return (
      <div className="flex flex-col gap-6 pt-[34px]">
        <PageHeader
          eyebrow="Athlete Mode"
          title={model.fullName || "Your profile"}
          info={<PlanBadge plan={plan} />}
          subtitle={notCompleted ? `Profile ${percentage}% complete` : "Score pending"}
          actions={
            <Btn variant="secondary" onClick={() => navigate("/athlete/profile/edit")}>
              Edit my profile
            </Btn>
          }
        />
        <Card className="flex max-w-[640px] flex-col gap-4">
          {notCompleted ? (
            <>
              <p className="bm-body-m text-bm-muted">Finish your profile setup to unlock your CacheScore™, valuation and brand matches.</p>
              {missingFields.length > 0 && (
                <ul className="flex flex-col gap-2">
                  {missingFields.map((field) => (
                    <li key={field} className="flex items-center gap-2 bm-body-m text-bm-ink">
                      <span className="h-[6px] w-[6px] rounded-full bg-bm-orange" />
                      {field}
                    </li>
                  ))}
                </ul>
              )}
              <div>
                <Btn onClick={() => navigate("/athlete/profile/edit")}>Complete profile</Btn>
              </div>
            </>
          ) : (
            <p className="bm-body-m text-bm-muted">Your CacheScore™ is being calculated. Check back shortly; your profile, deals and Circle keep working meanwhile.</p>
          )}
        </Card>
      </div>
    );
  }

  const share = () => {
    const url = model.athleteId ? `${window.location.origin}/p/${model.athleteId}` : window.location.href;
    if (navigator.share) {
      navigator.share({ title: model.fullName, url }).catch(() => {});
      return;
    }
    navigator.clipboard?.writeText(url).then(() => toast.success("Public profile link copied.")).catch(() => toast.error("Could not copy the link."));
  };

  const subtitle = profileSubtitle(model, athlete, cacheInformation);
  const editSection = (section) => navigate(section ? `/athlete/profile/edit?section=${section}` : "/athlete/profile/edit");
  // Every (i) opens the CacheValue in context modal: none without that
  // context (a PerformanceValue profile, no valuation yet; D3).
  const hasContext = Boolean(model.context);

  if (phone) {
    return (
      <ProfileBody
        model={model}
        athlete={athlete}
        cacheInformation={cacheInformation}
        matches={matches}
        premium={PREMIUM}
        selfView
        onEdit={editSection}
        overviewHeader={
          <ProfileIdentity
            model={model}
            subtitle={subtitle}
            badge={<PlanBadge plan={plan} />}
            actions={
              <>
                <Btn size="lg" className={PHONE_BTN} onClick={editProfile}>
                  Edit my profile
                </Btn>
                <Btn size="lg" variant="secondary" className={PHONE_BTN} onClick={share}>
                  Share profile
                </Btn>
              </>
            }
          />
        }
      />
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Athlete Mode"
        title={model.fullName || "Your profile"}
        info={<PlanBadge plan={plan} />}
        subtitle={subtitle}
        actions={
          <>
            <SocialLinks links={model.socialMedia} />
            <Btn variant="secondary" onClick={() => navigate("/athlete/profile/edit")}>
              Edit my profile
            </Btn>
            <Btn onClick={share}>Share profile</Btn>
          </>
        }
      />

      <ProfileBody
        model={model}
        athlete={athlete}
        cacheInformation={cacheInformation}
        matches={matches}
        premium={PREMIUM}
        selfView
        onInfo={hasContext ? () => setContextOpen(true) : null}
        onEdit={editSection}
        onManageCircle={() => navigate("/athlete/circle")}
        tab={tab}
        onTabChange={setTab}
      />

      {contextOpen && hasContext && <ValueContextModal model={model} onClose={() => setContextOpen(false)} />}
    </div>
  );
};

export default AthleteProfilePage;
